import { useReactiveVar } from '@apollo/client';
import { useLocation, useNavigate } from '@hulu/react-router-dom';
import React, { useContext, useMemo } from 'react';

import useGetAdAccountsByOrgId from '../../../apis/graphql/accountManagement/adAccount/useGetAdAccountsByOrgId';
import useFetchAdAccountByUser from '../../../apis/graphql/user/useFetchAdAccountByUser/useFetchAdAccountByUser';
import { RoutePath, selectedOrganizationIdVar, userAmsUserIdVar } from '../../../constants';
import { GhostModeContext } from '../../../context/GhostMode/GhostMode';
import useSetUserFromCurrentSession from '../../../hooks/useSetUserFromCurrentSession';
import { User } from '../../../utils/user';
import AdminUserCard from '../../AdminHeader/UserCard/UserCard';
import BellComponent from '../../BellComponent/BellComponent';
import { DropDownItem } from '../../types';
import AdAccountDropdown from '../AdAccountDropdown/AdAccountDropdown';
import { StyledLinkElement, Wrapper } from '../styled';
import UserCard from '../UserCard/UserCard';
import { getBrandsWithAdAccounts, getDisableTitle } from '../utils';

const RightBlock = (): JSX.Element => {
  const { isGhostMode } = useContext(GhostModeContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const selectedOrganizationId = useReactiveVar(selectedOrganizationIdVar) || '';
  const userAmsUserId = useReactiveVar(userAmsUserIdVar);
  const { userInfo } = useSetUserFromCurrentSession();
  const { adAccountsList: adAccountListByBrand, brandsData } = useFetchAdAccountByUser(
    userInfo?.ams_user_id || userAmsUserId,
    selectedOrganizationId,
    isGhostMode
  );
  const { data: adAccountList } = useGetAdAccountsByOrgId(selectedOrganizationId, isGhostMode);

  const pathToDisableAdAccountSwitcher = [
    RoutePath.AD_GALLERY,
    RoutePath.SUMMARY,
    RoutePath.REPORTS,
    'review',
    'lineItem',
    'payment',
  ];
  const shouldBeDisabledAdAccountSwitcher = pathToDisableAdAccountSwitcher.some((p: string) => pathname.includes(p));
  const disabledLabel = shouldBeDisabledAdAccountSwitcher ? getDisableTitle(pathname) : '';
  const brandsWithAdAccounts = useMemo(() => getBrandsWithAdAccounts(adAccountList), [adAccountList]);
  const idToken = User.getUserInfo()?.idToken;

  const showGhostModeHeader = isGhostMode && idToken;
  const shouldDisplayLoggedHeader = useMemo(
    () => (userInfo?.ams_user_id && selectedOrganizationId?.length) || showGhostModeHeader,
    [selectedOrganizationId?.length, showGhostModeHeader, userInfo?.ams_user_id]
  );

  const handleLoginClick = (): void => {
    navigate(RoutePath.LOGIN);
  };

  const onItemClick = (item: DropDownItem): void => {
    if (item.url) {
      navigate(RoutePath.ACCOUNT + item.url);
    }
  };

  if (shouldDisplayLoggedHeader) {
    return (
      <Wrapper>
        <AdAccountDropdown
          brandsData={brandsData}
          brandsWithAdAccounts={isGhostMode ? brandsWithAdAccounts : adAccountListByBrand}
          disabled={shouldBeDisabledAdAccountSwitcher}
          disabledLabel={disabledLabel}
        />
        {!isGhostMode && <BellComponent />}
        {showGhostModeHeader ? <AdminUserCard /> : <UserCard onItemClick={onItemClick} />}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <StyledLinkElement onClick={handleLoginClick} data-testid="loginButton" data-text="Login">
        Login
      </StyledLinkElement>
    </Wrapper>
  );
};

export default RightBlock;
